import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Check, CheckCircle, XCircle, ChevronRight, ArrowLeft, Star, Sparkles,
  Lock, Activity, Crown, Zap, X, Shield, Bot, BarChart3, Globe, Bell,
  MessageSquare, CalendarRange, HeartPulse, Radar, FileText,
  Grid3X3, Repeat, Brain, Layers, GitBranch, Copy, PieChart, Eye,
  TrendingUp, Clock, DollarSign, ArrowUp, Info, Building2,
} from 'lucide-react';
import logo from '../assets/images/haythix-logo.png';
import { TIER_CONFIG, TIER_ORDER, FEATURE_MATRIX } from '../constants/tiers';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import Seo from '../components/Seo';
import { useAuth } from '../hooks/useAuth';
import PayTierWithCrypto from '../components/PayTierWithCrypto';
import { createTrialCheckout } from '../services/api';

// Map summary icon strings to lucide-react components
const ICON_MAP = {
  Grid3X3, Globe, Zap, Repeat, Brain, BarChart3, Radio: Bell, Bot,
  Crown, TrendingUp, Layers, GitBranch, Copy, PieChart, Eye, Lock,
  Shield, Sparkles, Activity, Clock, DollarSign, Star, Building2,
};

// ─── Plan cards — derived from TIER_CONFIG so all features live in one place ─

const PLAN_META = {
  starter: { icon: Zap,       borderColor: 'border-blue-500/30',   bgHover: 'hover:border-blue-500/50',   accentColor: 'text-blue-400',   badge: null,             highlight: false, exclusive: false },
  trader:  { icon: Shield,    borderColor: 'border-cyan-500/30',   bgHover: 'hover:border-cyan-500/50',   accentColor: 'text-cyan-400',   badge: 'Most Popular',   highlight: true,  exclusive: false },
  pro:     { icon: Crown,     borderColor: 'border-violet-500/30', bgHover: 'hover:border-violet-500/50', accentColor: 'text-violet-400', badge: 'Professional',   highlight: false, exclusive: false },
  elite:   { icon: Sparkles,  borderColor: 'border-amber-400/40',  bgHover: 'hover:border-amber-400/60',  accentColor: 'text-amber-400',  badge: 'Exclusive',      highlight: false, exclusive: true  },
};

const PLANS = ['starter', 'trader', 'pro', 'elite'].map(id => {
  const cfg = TIER_CONFIG[id];
  const meta = PLAN_META[id];
  return {
    id,
    label: cfg.label,
    annualPrice: cfg.annualPrice,
    monthlyPrice: cfg.monthlyPrice,
    tagline: cfg.tagline,
    summary: cfg.summary || [],
    upgradeAdds: cfg.upgradeAdds || [],
    ...meta,
  };
});

// ─── Comparison table ────────────────────────────────────────────────────────

const COMPARISON = [
  // Trading
  { feature: 'Strategies',           starter: 'Basic',   trader: 'Advanced', pro: 'All',        elite: 'All + Exclusive' },
  { feature: 'Chains',               starter: 'ETH + Base', trader: '7 EVM chains', pro: '7 EVM chains', elite: '8 chains + Solana' },
  { feature: 'Copy Trading',         starter: false,     trader: true,       pro: true,         elite: true },
  { feature: 'AI Strategy Builder',  starter: false,     trader: false,      pro: true,         elite: true },
  { feature: 'Backtesting',          starter: false,     trader: false,      pro: true,         elite: true },
  // Community
  { feature: 'Community Chat',       starter: 'Public',  trader: 'Public + Trader+', pro: 'All + Pro room', elite: 'All + Elite + Whale' },
  { feature: 'Daily Streaks',        starter: true,      trader: true,       pro: true,         elite: true },
  { feature: 'Reactions (18 crypto)', starter: true,     trader: true,       pro: true,         elite: '18 + 2 Elite-only' },
  { feature: '@Mentions & Replies',  starter: true,      trader: true,       pro: true,         elite: true },
  { feature: 'Verified Call Tracking', starter: false,   trader: true,       pro: true,         elite: true },
  { feature: 'Polls & Predictions',  starter: 'Vote',    trader: 'Create',   pro: 'Create',    elite: 'Create' },
  { feature: 'Follow & Leaderboards', starter: false,    trader: true,       pro: true,         elite: true },
  { feature: 'USDC Tips (send/receive)', starter: false, trader: true,       pro: true,         elite: true },
  { feature: 'Server-Verified Trade Snapshots',  starter: true,  trader: true, pro: true, elite: true },
  { feature: 'Voice Notes in Chat (30s)',        starter: true,  trader: true, pro: true, elite: true },
  { feature: 'Top Calls of the Week (auto-pinned)', starter: true, trader: true, pro: true, elite: true },
  { feature: '🔥 Hot Channel Badge',              starter: true,  trader: true, pro: true, elite: true },
  { feature: 'Typing Indicators',                starter: true,  trader: true, pro: true, elite: true },
  { feature: 'Tip Read Receipts (✓ seen)',       starter: true,  trader: true, pro: true, elite: true },
  { feature: 'Auto Trade-Call Detection',         starter: true,  trader: true, pro: true, elite: true },
  { feature: 'Verified Caller Badge',             starter: true,  trader: true, pro: true, elite: true },
  { feature: 'Paid Channels (earn 80% USDC)',     starter: false, trader: false, pro: true, elite: true },
  { feature: 'Create Channels',      starter: false,     trader: false,      pro: true,         elite: true },
  { feature: 'Project Verification', starter: false,     trader: false,      pro: '30+ days',   elite: '30+ days' },
  { feature: 'AI Room Summaries',    starter: false,     trader: false,      pro: true,         elite: true },
  // Crypto-Native Payments (pay platform fees in USDC, no card needed)
  { feature: 'Pay Subscription in USDC (no card)', starter: true, trader: true, pro: true, elite: true },
  { feature: 'Multi-chain (Base/Polygon/Arbitrum)', starter: true, trader: true, pro: true, elite: true },
  { feature: 'On-Chain Auto-Renew (USDC allowance)', starter: true, trader: true, pro: true, elite: true },
  { feature: 'Server-Verified On-Chain Payments', starter: true, trader: true, pro: true, elite: true },
  // Engineering integrity — transparency as a feature
  { feature: 'Position Integrity + Drift Detection', starter: true, trader: true, pro: true, elite: true },
  { feature: 'Dual-View Accounting (operational + tax)', starter: false, trader: true, pro: true, elite: true },
  { feature: 'Outbound Webhooks (Discord/Telegram)', starter: false, trader: true, pro: true, elite: true },
  { feature: 'Real Backtests Only (no fake scenarios)', starter: true, trader: true, pro: true, elite: true },
  // Elite Suite — 17 institutional tools no other platform has
  // Trader's Suite (pro-trader power tooling)
  { feature: 'Multi-Account Aggregator (CEX + DEX unified)', starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Tax-Loss Harvest Engine',                       starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Institutional Reports (Bloomberg-grade PDFs)',  starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'AI Strategy Coach (grounded in YOUR data)',     starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Quant Lab (factor models + signal correlations)', starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Uniswap V3 LP Studio (range optimizer + IL calc)', starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Token Launch Scanner (24/7 honeypot detection)', starter: false, trader: false, pro: false, elite: true, killer: true },
  // Issuer Tools
  { feature: 'DEX Liquidity Optimizer',                        starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Listing Readiness Scorecard (5 major CEXs)',    starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Launch Volume Forecaster',                       starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Launch Roadmap Planner (4 phased templates)',    starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Volume Health Widget (embeddable SVG)',          starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Competitor Activity Tracker',                    starter: false, trader: false, pro: false, elite: true, killer: true },
  // Tokenomics
  { feature: 'AI Tokenomics Advisor (PDF whitepaper analysis)', starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Tokenomics Studio (whitepaper-ready output)',     starter: false, trader: false, pro: false, elite: true, killer: true },
  // Exclusive access
  { feature: 'Elite Lounge + Whale Club (private rooms)',      starter: false, trader: false, pro: false, elite: true, killer: true },
  { feature: 'Direct Founder Access + Elite-only reactions',    starter: false, trader: false, pro: false, elite: true, killer: true },
];

// ─── FAQ ─────────────────────────────────────────────────────────────────────

const FAQ = [
  { q: 'Can I switch plans anytime?', a: 'Yes. Upgrade instantly with prorated billing. Downgrade takes effect at the end of your current billing cycle. No double charges.' },
  { q: 'What happens after my 14-day trial?', a: 'Your credit card is automatically charged for the plan you selected. Cancel anytime before the trial ends to avoid charges.' },
  { q: 'Do you offer refunds?', a: 'Yes, we offer a 14-day money-back guarantee on all plans. No questions asked.' },
  { q: 'What payment methods do you accept?', a: 'All major credit/debit cards via Stripe. USDC crypto tips are also supported for community tipping.' },
  { q: 'What is the Founders Program?', a: 'The first 100 paying users get permanent 50% off any plan, forever. Even if prices increase. Each founder gets a numbered badge displayed in chat.' },
  { q: 'Who can submit projects for verification?', a: 'Pro and Elite tier members with accounts at least 30 days old. This prevents trial abuse — verification is a premium feature.' },
  { q: 'What community features are included?', a: 'All tiers get chat, streaks, reactions, and mentions. Trader+ adds call tracking, tips, polls. Pro adds channels and project verification. Elite gets exclusive rooms and 6 killer features.' },
  { q: 'What are the Elite exclusive rooms?', a: 'Elite Lounge (direct founder access, weekly AMA) and Whale Club (large position strategies, OTC discussion). Plus elite-only reactions (✨ Verified Bullish, 🐋 Whale Alert).' },
];

// ─── Component ───────────────────────────────────────────────────────────────

export default function PricingPage() {
  const [billing, setBilling] = useState('annual');
  const [openFaq, setOpenFaq] = useState(null);
  const [founders, setFounders] = useState({ total: 100, claimed: 0, remaining: 100, founders: [] });
  const [showUsdcCheckout, setShowUsdcCheckout] = useState(null);
  const [usdcCheckout, setUsdcCheckout] = useState(null);
  const [claimingFounder, setClaimingFounder] = useState(false);
  const [founderClaimed, setFounderClaimed] = useState(null);
  const [showFounderPicker, setShowFounderPicker] = useState(false);
  const [toast, setToast] = useState(null);
  const [detailTier, setDetailTier] = useState(null);
  const navigate = useNavigate();
  const isAnnual = billing === 'annual';
  // Auth state — use the same source-of-truth as PublicNav/Layout so the CTAs
  // and nav agree. A raw localStorage check disagrees when the token is stale
  // or invalidated; this hook revalidates on mount + on focus.
  const { isLoggedIn, user } = useAuth();
  const authToken = localStorage.getItem('authToken');

  // Helper: routes the user to the right place based on auth state + path.
  //
  // The critical case is `isLoggedIn && user.tier === 'pending'` — these users
  // signed up but never completed Stripe Checkout, so they have a token but no
  // active subscription. They need to be sent DIRECTLY to Stripe Checkout to
  // capture their card and activate the trial — NOT to /settings (blank page
  // because they have no subscription to manage yet).
  async function handleTierAction(planId, path) {
    const isPending = user?.tier === 'pending';

    // Pending users: every tier click must go to Stripe Checkout with the trial
    // flow, just like a brand-new signup would. Same code path as RegisterPage
    // post-signup handler — keeps the entire signup-to-trial flow consistent.
    if (isLoggedIn && isPending && (path === 'stripe' || path === 'trial')) {
      try {
        const data = await createTrialCheckout(planId, 'monthly');
        if (data?.checkoutUrl) {
          window.location.href = data.checkoutUrl;
          return;
        }
        setToast('Could not open checkout. Please try again or contact support.');
      } catch (err) {
        console.error('Trial checkout error:', err);
        setToast(`Checkout error: ${err.message || 'Unknown'}. Please try again.`);
      }
      return;
    }

    if (isLoggedIn && path === 'stripe') {
      // Existing subscriber upgrading — go to settings subscription tab
      navigate(`/settings?tab=subscription&plan=${planId}`);
    } else if (isLoggedIn && path === 'usdc') {
      // Logged in — show USDC checkout right here (works for pending + active alike)
      setShowUsdcCheckout(planId);
    } else if (isLoggedIn && path === 'trial') {
      // Logged-in non-pending user clicking Trial — already on a tier, nothing to do
      navigate(`/settings?tab=subscription&plan=${planId}`);
    } else {
      // Not logged in — go to register with plan + path (existing happy path)
      navigate(`/register?plan=${planId}&path=${path}`);
    }
  }

  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL || '/api'}/founders/status`)
      .then(r => r.json())
      .then(d => { if (d?.data) setFounders(d.data); })
      .catch(() => {});
  }, []);

  const pricingJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'haythix crypto trading platform',
    description: 'Multi-chain DEX + CEX trading SaaS — bots, copy trading, arbitrage, tokenomics tools.',
    brand: { '@type': 'Brand', name: 'haythix' },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '0',
      highPrice: '199.99',
      offerCount: '5',
      offers: [
        { '@type': 'Offer', name: 'Trial',   price: '0',      priceCurrency: 'USD', description: '14-day free trial — all features' },
        { '@type': 'Offer', name: 'Starter', price: '10',     priceCurrency: 'USD', description: 'Starter monthly' },
        { '@type': 'Offer', name: 'Trader',  price: '49.99',  priceCurrency: 'USD', description: 'Trader monthly' },
        { '@type': 'Offer', name: 'Pro',     price: '75.99',  priceCurrency: 'USD', description: 'Pro monthly' },
        { '@type': 'Offer', name: 'Elite',   price: '149.99', priceCurrency: 'USD', description: 'Elite monthly' },
      ],
    },
  };

  return (
    <div className="min-h-screen bg-[#0a0b0f] text-white">
      <Seo
        title="Pricing — from $10/mo with 14-day free trial"
        description="5 plans for crypto traders: Trial (free 14d), Starter $10/mo, Trader $49.99/mo, Pro $75.99/mo, Elite $149.99/mo. Pay by card or USDC. First 100 founders save 50% forever."
        path="/pricing"
        jsonLd={pricingJsonLd}
      />
      <PublicNav />

      {/* Pending-tier banner — shown when a user signed up but has not completed
          Stripe Checkout (no card on file, no trial active). Tells them what
          to do; the App.jsx ProtectedRoute also bounces them here from every
          other in-app route until they pick a plan and add a card. */}
      {isLoggedIn && user?.tier === 'pending' && (
        <div className="mx-auto max-w-3xl mt-6 px-4">
          <div className="rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-dark-800 to-dark-900 p-5 flex items-start gap-3">
            <Activity className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-amber-300 font-semibold text-sm">Complete your subscription to unlock the platform</p>
              <p className="text-gray-300 text-sm mt-1 leading-relaxed">
                Pick a plan below to start your 14-day free trial. We collect a card so the trial converts automatically — no charge for 14 days, cancel anytime.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Hero */}
      <div className="text-center pt-16 pb-6 px-4">
        {/* Founders Program Banner */}
        {founders.remaining > 0 && (
          <div className="max-w-2xl mx-auto mb-8 rounded-2xl overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(16,16,20,1) 50%, rgba(139,92,246,0.08) 100%)', border: '1px solid rgba(245,158,11,0.2)' }}>
            <div className="h-1 bg-gradient-to-r from-amber-500/60 via-yellow-400/80 to-amber-500/60" />
            <div className="p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <span className="text-3xl">🏆</span>
                <h3 className="text-xl font-bold text-white">Founders Program</h3>
              </div>
              <p className="text-sm text-gray-400 text-center mb-4">
                The first <span className="text-amber-400 font-bold">100 paying users</span> become haythix Founders —
                <span className="text-white font-semibold"> permanent 50% off</span> any tier, forever. Even if prices increase.
              </p>

              {/* Counter */}
              <div className="flex items-center justify-center gap-6 mb-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-amber-400">{founders.remaining}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Spots Left</p>
                </div>
                <div className="h-12 w-px bg-dark-700" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-white">{founders.claimed}</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Claimed</p>
                </div>
                <div className="h-12 w-px bg-dark-700" />
                <div className="text-center">
                  <p className="text-3xl font-bold text-emerald-400">50%</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider">Off Forever</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="bg-dark-700 rounded-full h-2.5 mb-3 overflow-hidden">
                <div className="bg-gradient-to-r from-amber-500 to-yellow-400 h-full rounded-full transition-all"
                  style={{ width: `${(founders.claimed / founders.total) * 100}%` }} />
              </div>
              <p className="text-[10px] text-gray-600 text-center">{founders.claimed} of {founders.total} founder slots claimed</p>

              {/* Founder perks */}
              <div className="grid grid-cols-3 gap-2 mt-4">
                <div className="bg-dark-700/50 rounded-xl p-2.5 text-center">
                  <span className="text-lg block">🏆</span>
                  <p className="text-[10px] text-gray-400 mt-0.5">Numbered Founder Badge</p>
                </div>
                <div className="bg-dark-700/50 rounded-xl p-2.5 text-center">
                  <span className="text-lg block">💰</span>
                  <p className="text-[10px] text-gray-400 mt-0.5">50% Off All Plans Forever</p>
                </div>
                <div className="bg-dark-700/50 rounded-xl p-2.5 text-center">
                  <span className="text-lg block">⭐</span>
                  <p className="text-[10px] text-gray-400 mt-0.5">Founder Status in Chat</p>
                </div>
              </div>

              {/* Claim button */}
              {!founderClaimed ? (
                <>
                  <button onClick={async () => {
                    if (!isLoggedIn) {
                      setShowFounderPicker(true);
                      return;
                    }
                    setClaimingFounder(true);
                    try {
                      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/founders/claim`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` }, body: '{}',
                      });
                      const data = await res.json();
                      if (data?.data?.claimed || data?.data?.alreadyClaimed) {
                        setFounderClaimed(data.data.number);
                        setFounders(f => ({ ...f, claimed: f.claimed + (data.data.alreadyClaimed ? 0 : 1), remaining: Math.max(0, f.remaining - (data.data.alreadyClaimed ? 0 : 1)) }));
                        setToast(`🏆 Founder #${data.data.number} claimed! 50% off applied to all plans below.`);
                        setTimeout(() => setToast(null), 5000);
                      }
                    } catch {}
                    setClaimingFounder(false);
                  }} disabled={claimingFounder}
                    className="w-full py-3 rounded-xl text-sm font-bold bg-gradient-to-r from-amber-500 to-yellow-400 text-dark-900 hover:from-amber-400 hover:to-yellow-300 disabled:opacity-50 transition-all mt-3">
                    {claimingFounder ? 'Claiming...' : isLoggedIn ? '🏆 Claim Your Founder Slot — 50% Off Forever' : '🏆 Sign Up & Claim Founder Slot'}
                  </button>
                  <p className="text-[9px] text-gray-600 text-center mt-1">
                    {isLoggedIn ? 'Claim first, then subscribe below with your 50% discount' : 'Pick your plan below → 50% off forever'}

                  {/* Founder tier picker */}
                  {showFounderPicker && !isLoggedIn && (
                    <div className="mt-3 bg-dark-800/50 rounded-xl p-3 border border-amber-500/20 space-y-2">
                      <p className="text-xs text-white font-semibold text-center mb-2">Choose your plan with 50% Founder discount:</p>
                      {PLANS.map(p => (
                        <button key={p.id} onClick={() => navigate(`/register?plan=${p.id}&path=stripe&founder=true`)}
                          className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-dark-700/50 hover:bg-amber-500/10 border border-dark-600 hover:border-amber-500/30 transition-all">
                          <span className="text-sm font-semibold text-white">{p.label}</span>
                          <div className="text-right">
                            <span className="text-xs text-gray-500 line-through">${isAnnual ? p.annualPrice : p.monthlyPrice}/mo</span>
                            <span className="text-sm font-bold text-amber-400 ml-2">${((isAnnual ? p.annualPrice : p.monthlyPrice) / 2).toFixed(2)}/mo</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  </p>
                </>
              ) : (
                <div className="mt-3 bg-amber-500/10 rounded-xl p-3 text-center border border-amber-500/20">
                  <p className="text-sm font-bold text-amber-400">🏆 You are Founder #{founderClaimed}!</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">50% permanent discount applied to all plans below</p>
                </div>
              )}

              {/* Recent founders */}
              {founders.founders.length > 0 && (
                <div className="mt-4 pt-3 border-t border-dark-700/50">
                  <p className="text-[10px] text-gray-500 text-center mb-2">Recent Founders</p>
                  <div className="flex justify-center gap-2 flex-wrap">
                    {founders.founders.slice(-5).map(f => (
                      <span key={f.number} className="text-[10px] font-bold px-2 py-1 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/20">
                        🏆 #{f.number} {f.userName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {/* Anchor id="plans" — PublicNav "Complete Subscription" + LandingPage CTAs
            link to /pricing#plans and the page scrolls to the tier cards. Without
            this, clicking the nav button while already on /pricing did nothing. */}
        <h1 id="plans" className="text-4xl md:text-5xl font-bold mb-4 scroll-mt-20">
          Choose the plan that <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">fits your ambition</span>
        </h1>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-6">
          Lock in these rates before they increase. Annual members keep their price forever.
        </p>

        {/* Transparent-methodology banner — describes what we publish, NOT what users will earn.
            Every claim here is a fact about haythix's documentation discipline, not a promise
            about user outcomes. */}
        <div className="max-w-3xl mx-auto mb-10 bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 flex items-start gap-3 text-left">
          <span className="text-emerald-400 text-xl mt-0.5">📖</span>
          <div className="flex-1 text-sm">
            <div className="text-white font-medium">All tiers include all 7 signature Verticals</div>
            <p className="text-gray-400 mt-0.5">
              Every Vertical's methodology is published on a public API endpoint anyone can read without an account.
              We document how our tools work — including their limits and known data gaps. Not a promise of profit;
              not financial advice. Just transparent methodology.
            </p>
          </div>
        </div>

        {/* Billing toggle */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <span className={`text-sm font-medium ${!isAnnual ? 'text-white' : 'text-gray-500'}`}>Monthly</span>
          <button
            onClick={() => setBilling(b => b === 'annual' ? 'monthly' : 'annual')}
            className={`relative w-14 h-7 rounded-full transition-colors ${isAnnual ? 'bg-emerald-500' : 'bg-dark-600'}`}
          >
            <span className={`absolute top-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform ${isAnnual ? 'left-[30px]' : 'left-0.5'}`} />
          </button>
          <span className={`text-sm font-medium ${isAnnual ? 'text-white' : 'text-gray-500'}`}>Annual</span>
          {isAnnual && (
            <span className="text-xs font-bold text-emerald-400 bg-emerald-500/15 px-2 py-0.5 rounded-full border border-emerald-500/30">
              Save up to 50%
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 mb-12">Prices increase in 6 months — annual users keep their locked-in rate</p>
      </div>

      {/* Pricing cards */}
      <div className="max-w-6xl mx-auto px-4 pb-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
        {PLANS.map(plan => {
          const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
          const savingsPercent = Math.round((1 - plan.annualPrice / plan.monthlyPrice) * 100);
          const annualTotal = Math.round(plan.annualPrice * 12);
          const Icon = plan.icon;

          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-6 flex flex-col transition-all duration-200 ${plan.borderColor} ${plan.bgHover} ${
                plan.highlight ? 'bg-gradient-to-b from-cyan-500/5 to-transparent ring-1 ring-cyan-400/20' :
                plan.exclusive ? 'bg-gradient-to-b from-amber-500/5 to-transparent ring-1 ring-amber-400/20' :
                'bg-[#0d0e14]'
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
                    plan.exclusive ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900'
                      : plan.highlight ? 'bg-cyan-400 text-gray-900'
                      : 'bg-violet-400 text-gray-900'
                  }`}>{plan.badge}</span>
                </div>
              )}

              {/* Header */}
              <div className="mb-5">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-5 h-5 ${plan.accentColor}`} />
                  <h3 className="font-bold text-white text-lg">{plan.label}</h3>
                </div>
                <p className="text-xs text-gray-500 leading-relaxed">{plan.tagline}</p>
              </div>

              {/* Price */}
              <div className="mb-5">
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-bold text-white">${price}</span>
                  <span className="text-gray-500 text-sm">/mo</span>
                </div>
                {isAnnual ? (
                  <div className="mt-1.5">
                    <span className="text-xs text-gray-500 line-through">${plan.monthlyPrice}/mo</span>
                    <span className="ml-1.5 text-xs text-emerald-400 font-semibold">Save {savingsPercent}%</span>
                    <p className="text-[11px] text-gray-600 mt-0.5">Billed ${annualTotal}/year</p>
                  </div>
                ) : (
                  <p className="text-xs text-emerald-400 mt-1.5">${plan.annualPrice}/mo billed annually</p>
                )}
              </div>

              {/* Top features (summary) */}
              <ul className="space-y-2 mb-4">
                {plan.summary.map((f, i) => {
                  const SummaryIcon = ICON_MAP[f.icon] || CheckCircle;
                  return (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                      <SummaryIcon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${plan.accentColor}`} />
                      <span>{f.label}</span>
                    </li>
                  );
                })}
              </ul>

              {/* See all features button */}
              <button
                onClick={() => setDetailTier(plan.id)}
                className={`w-full text-xs font-semibold py-2 mb-4 rounded-lg border border-dark-600/60 hover:border-dark-500 transition-colors flex items-center justify-center gap-1.5 ${plan.accentColor}`}
              >
                <Info className="w-3.5 h-3.5" /> See all features
              </button>

              {/* Upgrade preview (greyed) */}
              {plan.upgradeAdds.length > 0 && (
                <div className="border-t border-dark-700/50 pt-3 mb-4 flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-gray-600 font-semibold mb-2 flex items-center gap-1">
                    <ArrowUp className="w-3 h-3" /> Upgrade adds
                  </p>
                  <ul className="space-y-1.5">
                    {plan.upgradeAdds.map((f, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-gray-500">
                        <Lock className="w-3 h-3 flex-shrink-0 mt-0.5 text-gray-600" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {plan.upgradeAdds.length === 0 && <div className="flex-1" />}

              {/* Payment Options */}
              <div className="space-y-2">
                {/* Primary CTA. Three states:
                    - logged out → "Start 14-Day Free Trial" → /register?plan=X&path=trial
                    - logged in + pending → "Start 14-Day Free Trial" → direct to Stripe Checkout
                      (they just signed up, no card on file yet — same as a fresh signup would)
                    - logged in + paying tier → "Upgrade to X" → settings tab for prorated upgrade */}
                <button
                  onClick={() => handleTierAction(plan.id, (isLoggedIn && user?.tier !== 'pending') ? 'stripe' : 'trial')}
                  className={`w-full py-3 rounded-xl font-semibold text-sm transition-all ${
                    plan.exclusive
                      ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 hover:from-amber-300 hover:to-orange-300'
                      : plan.highlight
                      ? 'bg-white text-gray-900 hover:bg-gray-100'
                      : 'border border-white/10 text-white hover:bg-white/5'
                  }`}
                >
                  {(!isLoggedIn || user?.tier === 'pending') ? 'Start 14-Day Free Trial' : `Upgrade to ${plan.label}`}
                </button>
                {(!isLoggedIn || user?.tier === 'pending') && (
                  <p className="text-[9px] text-gray-600 text-center -mt-0.5">Credit card required · Auto-converts to paid · Cancel anytime</p>
                )}

                {/* Secondary: Pay Now (skip trial) — only for new users (logged-out) */}
                {!isLoggedIn && (
                  <button
                    onClick={() => handleTierAction(plan.id, 'stripe')}
                    className="w-full py-2.5 rounded-xl text-xs font-medium text-gray-400 hover:text-white border border-white/5 hover:bg-white/5 transition-all flex items-center justify-center gap-1.5"
                  >
                    💳 Pay Now — Skip Trial
                  </button>
                )}

                {/* Crypto payment option — primary for crypto-native users, secondary for everyone else */}
                {plan.id !== 'starter' || true ? (
                  <button
                    onClick={() => handleTierAction(plan.id, 'usdc')}
                    className="w-full py-2.5 rounded-xl text-xs font-medium text-emerald-300 hover:text-emerald-200 border border-emerald-500/20 hover:border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10 transition-all flex items-center justify-center gap-1.5"
                  >
                    ⚡ Pay with Crypto (USDC, no card needed)
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {/* Comparison table — anchored so the modal's "Compare all tiers" button scrolls here */}
      <div id="tier-comparison" className="max-w-6xl mx-auto px-4 pb-20 scroll-mt-20">
        <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">
          Full <span className="text-amber-400">feature comparison</span>
        </h2>
        <p className="text-gray-500 text-center mb-10 text-sm">See exactly what you get with each plan</p>

        <div className="overflow-x-auto rounded-2xl border border-dark-700/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-dark-700/50 bg-dark-800/50">
                <th className="text-left px-5 py-4 text-gray-400 font-medium">Feature</th>
                {['Starter', 'Trader', 'Pro', 'Elite'].map(t => (
                  <th key={t} className={`px-4 py-4 text-center font-semibold ${
                    t === 'Elite' ? 'text-amber-400' : 'text-white'
                  }`}>{t}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((row, i) => (
                <tr key={row.feature} className={`border-b border-dark-700/30 ${row.killer ? 'bg-amber-400/[0.03]' : ''}`}>
                  <td className={`px-5 py-3 ${row.killer ? 'text-amber-400 font-medium' : 'text-gray-300'}`}>
                    {row.killer && <Star className="w-3 h-3 inline mr-1.5 text-amber-400 fill-amber-400" />}
                    {row.feature}
                  </td>
                  {['starter', 'trader', 'pro', 'elite'].map(tier => {
                    const val = row[tier];
                    return (
                      <td key={tier} className="px-4 py-3 text-center">
                        {val === true ? <CheckCircle className="w-4 h-4 text-emerald-400 mx-auto" /> :
                         val === false ? <span className="text-gray-700">—</span> :
                         <span className={`text-xs font-medium ${tier === 'elite' && row.killer ? 'text-amber-400' : 'text-gray-300'}`}>{val}</span>}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Trust badges */}
      <div className="max-w-4xl mx-auto px-4 pb-16">
        <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-500">
          {[
            ['7-day free trial', 'Try before you commit'],
            ['Cancel anytime', 'No contracts or lock-in'],
            ['14-day money-back', 'Full refund, no questions'],
            ['Bank-grade security', 'Non-custodial, MFA, encrypted'],
          ].map(([title, sub]) => (
            <div key={title} className="flex items-center gap-2.5 px-4 py-3 rounded-xl bg-dark-800/50 border border-dark-700/30">
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <div>
                <p className="text-white text-xs font-semibold">{title}</p>
                <p className="text-[11px] text-gray-500">{sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-3xl mx-auto px-4 pb-20">
        <h2 className="text-2xl font-bold text-center mb-8">Frequently asked questions</h2>
        <div className="space-y-3">
          {FAQ.map((item, i) => (
            <div key={i} className="rounded-xl border border-dark-700/40 bg-dark-800/30 overflow-hidden">
              <button
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full flex items-center justify-between px-5 py-4 text-left text-sm font-medium text-white hover:text-accent-primary transition-colors"
              >
                {item.q}
                <ChevronRight className={`w-4 h-4 text-gray-500 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-90' : ''}`} />
              </button>
              {openFaq === i && (
                <div className="px-5 pb-4 text-sm text-gray-400 leading-relaxed">
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="rounded-2xl bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-violet-500/10 border border-amber-400/20 p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready to trade smarter?</h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto">
            Join thousands of traders using AI-powered automation to grow their portfolio. Start free, upgrade when you're ready.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={() => navigate('/register?plan=trial')}
              className="px-6 py-3 rounded-xl bg-white text-gray-900 font-semibold text-sm hover:bg-gray-100 transition-colors"
            >
              Start 7-Day Free Trial
            </button>
            <button
              onClick={() => navigate('/register?plan=elite')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 font-semibold text-sm hover:from-amber-300 hover:to-orange-300 transition-all"
            >
              Go Elite
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <PublicFooter />

      {/* USDC Checkout Modal — new wagmi-driven one-click multi-chain flow */}
      {showUsdcCheckout && isLoggedIn && (
        <PayTierWithCrypto
          tier={showUsdcCheckout}
          period={isAnnual ? 'annual' : 'monthly'}
          onSuccess={() => {
            setShowUsdcCheckout(null);
            setUsdcCheckout(null);
            setToast('✅ Tier upgraded! Refresh to see all unlocked features.');
            setTimeout(() => setToast(null), 5000);
          }}
          onClose={() => { setShowUsdcCheckout(null); setUsdcCheckout(null); }}
        />
      )}

      {/* Legacy USDC modal — only used for not-logged-in users (registration funnel) */}
      {showUsdcCheckout && !isLoggedIn && (() => {
        const plan = PLANS.find(p => p.id === showUsdcCheckout);
        if (!plan) return null;
        const price = isAnnual ? plan.annualPrice : plan.monthlyPrice;
        const period = isAnnual ? 'annual' : 'monthly';
        const founderDiscount = founderClaimed ? 50 : 0;
        const finalPrice = +(price * (1 - founderDiscount / 100)).toFixed(2);

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/70" onClick={() => { setShowUsdcCheckout(null); setUsdcCheckout(null); }} />
            <div className="relative bg-[#0f1015] rounded-2xl border border-dark-700/50 shadow-2xl w-full max-w-md overflow-hidden">
              <div className="h-1 bg-gradient-to-r from-emerald-500/60 via-green-400/80 to-emerald-500/60" />
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-bold text-white">💰 Pay with USDC</h2>
                  <button onClick={() => { setShowUsdcCheckout(null); setUsdcCheckout(null); }} className="text-gray-500 hover:text-white text-xl">×</button>
                </div>

                {!usdcCheckout ? (
                  <>
                    <div className="bg-dark-800 rounded-xl p-4 border border-dark-700/50 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-white">{plan.label} Plan</p>
                          <p className="text-[10px] text-gray-500">{period === 'annual' ? 'Annual' : 'Monthly'} subscription</p>
                        </div>
                        <div className="text-right">
                          {founderDiscount > 0 && <p className="text-[10px] text-gray-500 line-through">${price}</p>}
                          <p className="text-xl font-bold text-emerald-400">${finalPrice}</p>
                          <p className="text-[9px] text-gray-500">USDC</p>
                        </div>
                      </div>
                      {founderDiscount > 0 && (
                        <div className="mt-2 bg-amber-500/10 rounded-lg px-2 py-1 text-center">
                          <p className="text-[10px] font-bold text-amber-400">🏆 Founder discount: {founderDiscount}% off!</p>
                        </div>
                      )}
                    </div>

                    <p className="text-xs text-gray-500 mb-3">Select chain to pay on:</p>
                    <div className="grid grid-cols-4 gap-2 mb-4">
                      {[
                        { id: 'ethereum', name: 'Ethereum', icon: '⟠' },
                        { id: 'base', name: 'Base', icon: '🔵' },
                        { id: 'polygon', name: 'Polygon', icon: '🟣' },
                        { id: 'arbitrum', name: 'Arbitrum', icon: '🔷' },
                      ].map(c => (
                        <button key={c.id} onClick={async () => {
                          if (!isLoggedIn) {
                            // Not logged in — show payment info directly + prompt to register
                            setUsdcCheckout({
                              finalPrice: founderClaimed ? +(price * 0.5).toFixed(2) : price,
                              chain: c.id,
                              payToWallet: '0x3A6163B029081e1C4b94CC98f2A6895CA314D76a',
                              memo: `sub-${plan.id}-${Date.now().toString(36)}`,
                              needsRegistration: true,
                              tier: plan.id,
                            });
                            return;
                          }
                          try {
                            const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/usdc-checkout/create`, {
                              method: 'POST', headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                              body: JSON.stringify({ tier: plan.id, chain: c.id, period }),
                            });
                            const data = await res.json();
                            if (data?.data) setUsdcCheckout(data.data);
                          } catch {}
                        }}
                          className="py-3 rounded-xl text-xs font-medium bg-dark-800 text-gray-400 hover:text-emerald-400 hover:bg-emerald-500/5 border border-dark-700 hover:border-emerald-500/20 transition-all flex flex-col items-center gap-1">
                          <span className="text-lg">{c.icon}</span>
                          <span>{c.name}</span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/15 mb-4 text-center">
                      <p className="text-xs text-gray-400 mb-1">{plan.label} Plan</p>
                      <p className="text-2xl font-bold text-emerald-400">${usdcCheckout.finalPrice} USDC</p>
                      <p className="text-xs text-gray-500 mt-1">on {usdcCheckout.chain?.charAt(0).toUpperCase() + usdcCheckout.chain?.slice(1)}</p>
                    </div>

                    {/* One-click MetaMask pay */}
                    {window.ethereum && (
                      <button onClick={async () => {
                        try {
                          const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
                          if (!accounts?.[0]) return;

                          const USDC_ADDRS = { ethereum: '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48', base: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913', polygon: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', arbitrum: '0xaf88d065e77c8cC2239327C5EDb3A432268e5831' };
                          const CHAIN_IDS = { ethereum: '0x1', base: '0x2105', polygon: '0x89', arbitrum: '0xa4b1' };
                          const chain = usdcCheckout.chain || 'ethereum';
                          const usdcAddr = USDC_ADDRS[chain];
                          const treasury = '0x3A6163B029081e1C4b94CC98f2A6895CA314D76a';
                          const amt = BigInt(Math.round(usdcCheckout.finalPrice * 1e6));

                          try { await window.ethereum.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: CHAIN_IDS[chain] }] }); } catch {}

                          const data = '0xa9059cbb' + treasury.slice(2).padStart(64, '0') + amt.toString(16).padStart(64, '0');
                          const txHash = await window.ethereum.request({
                            method: 'eth_sendTransaction',
                            params: [{ from: accounts[0], to: usdcAddr, data, value: '0x0' }],
                          });

                          setUsdcCheckout(prev => ({ ...prev, txSent: true, txHash }));

                          if (!usdcCheckout.needsRegistration) {
                            setToast('✅ Payment sent! Your plan will activate within 5 minutes.');
                            setTimeout(() => setToast(null), 5000);
                          }
                        } catch (err) {
                          if (err.code !== 4001) setToast(err.message || 'Transaction failed');
                          setTimeout(() => setToast(null), 4000);
                        }
                      }}
                        className="w-full py-3 rounded-xl text-sm font-bold bg-emerald-500 text-dark-900 hover:bg-emerald-400 transition-all mb-3 flex items-center justify-center gap-2">
                        🦊 Pay ${usdcCheckout.finalPrice} USDC with MetaMask
                      </button>
                    )}

                    {usdcCheckout.txSent ? (
                      <div className="text-center space-y-3 mb-4">
                        <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
                          <span className="text-3xl block mb-2">✅</span>
                          <p className="text-sm font-bold text-emerald-400">Payment Sent!</p>
                          <p className="text-[10px] text-gray-500 font-mono break-all mt-1">{usdcCheckout.txHash}</p>
                        </div>
                        <p className="text-xs text-gray-400">Your {plan.label} plan activates within 5 minutes of confirmation.</p>
                        {usdcCheckout.needsRegistration && (
                          <button onClick={() => navigate(`/register?plan=${usdcCheckout.tier}&path=usdc`)}
                            className="w-full py-3 rounded-xl text-sm font-bold bg-accent-primary text-dark-900 hover:bg-accent-primary/90 transition-all">
                            Create Your Account Now
                          </button>
                        )}
                      </div>
                    ) : (
                      <>
                        <p className="text-[10px] text-gray-600 text-center mb-3">— or send manually —</p>
                        <div className="space-y-2 mb-3">
                          <div className="bg-dark-800 rounded-xl p-2.5">
                            <p className="text-[9px] text-gray-500 uppercase">Send to</p>
                            <p className="text-[10px] text-white font-mono break-all select-all">{usdcCheckout.payToWallet}</p>
                          </div>
                          <div className="bg-dark-800 rounded-xl p-2.5">
                            <p className="text-[9px] text-gray-500 uppercase">Memo</p>
                            <p className="text-xs text-accent-primary font-mono font-bold select-all">{usdcCheckout.memo}</p>
                          </div>
                        </div>
                      </>
                    )}

                    <button onClick={() => { setShowUsdcCheckout(null); setUsdcCheckout(null); }}
                      className="w-full py-2.5 rounded-xl text-xs text-gray-500 hover:text-white bg-dark-800 hover:bg-dark-700 transition-all">
                      Close
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 px-5 py-3 rounded-xl shadow-2xl text-sm font-medium bg-dark-700 text-white border border-amber-500/30">
          {toast}
        </div>
      )}

      {detailTier && (
        <TierDetailModal
          tierId={detailTier}
          onClose={() => setDetailTier(null)}
          onSelectPlan={(planId) => { setDetailTier(null); handleTierAction(planId, isLoggedIn ? 'stripe' : 'trial'); }}
        />
      )}
    </div>
  );
}

// ─── Tier Detail Modal ───────────────────────────────────────────────────────
// Click "See all features" on any pricing card → shows the full feature matrix
// for that tier, organized by category. ✓ for included, ✗ for not, with the
// row's exact tier value (e.g. "3/day" or "unlimited") next to it.

function TierDetailModal({ tierId, onClose, onSelectPlan }) {
  const cfg = TIER_CONFIG[tierId];
  if (!cfg) return null;

  // Group matrix rows by category
  const grouped = FEATURE_MATRIX.reduce((acc, row) => {
    if (!acc[row.category]) acc[row.category] = [];
    acc[row.category].push(row);
    return acc;
  }, {});

  function renderValue(v) {
    if (v === true)  return <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />;
    if (v === false) return <XCircle className="w-4 h-4 text-gray-700 flex-shrink-0" />;
    return <span className="text-xs text-emerald-400 font-mono whitespace-nowrap">{v}</span>;
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="relative bg-[#0d0e14] border border-dark-600/50 rounded-2xl max-w-2xl w-full my-8 max-h-[90vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-6 border-b border-dark-700/50 ${cfg.bgColor} rounded-t-2xl`}>
          <div className="flex items-start justify-between mb-2">
            <div>
              <p className={`text-xs uppercase tracking-widest font-semibold mb-1 ${cfg.color}`}>{cfg.label} Tier</p>
              <h2 className="text-2xl font-bold text-white">Everything You Get</h2>
              <p className="text-sm text-gray-400 mt-1">{cfg.tagline}</p>
            </div>
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-400">
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-baseline gap-2 mt-4">
            <span className="text-3xl font-bold text-white">${cfg.annualPrice}</span>
            <span className="text-sm text-gray-500">/mo billed annually</span>
            <span className="text-xs text-gray-600 ml-2">or ${cfg.monthlyPrice}/mo monthly</span>
          </div>
        </div>

        {/* Feature matrix by category */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {Object.entries(grouped).map(([category, rows]) => (
            <div key={category}>
              <h3 className="text-xs uppercase tracking-widest font-semibold text-gray-500 mb-3">{category}</h3>
              <div className="space-y-2">
                {rows.map(row => {
                  const value = row.tiers[tierId];
                  const isIncluded = value !== false;
                  return (
                    <div
                      key={row.key}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        isIncluded ? 'bg-dark-700/30 border-dark-600/40' : 'bg-dark-800/50 border-dark-700/40 opacity-50'
                      }`}
                    >
                      <div className="flex-shrink-0 mt-0.5">{renderValue(value)}</div>
                      <div className="flex-1">
                        <p className="text-sm font-medium text-white">{row.label}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{row.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="p-6 border-t border-dark-700/50 flex gap-3">
          <button
            type="button"
            onClick={() => {
              onClose();
              // Wait one tick for the modal unmount, then scroll to the comparison table.
              setTimeout(() => {
                document.getElementById('tier-comparison')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 0);
            }}
            className="flex-1 text-center text-sm py-2.5 rounded-xl border border-dark-600 text-gray-300 hover:bg-dark-700 transition-colors"
          >
            Compare all tiers
          </button>
          <button
            onClick={() => onSelectPlan(tierId)}
            className={`flex-1 text-sm font-semibold py-2.5 rounded-xl ${
              cfg.exclusive ? 'bg-gradient-to-r from-amber-400 to-orange-400 text-gray-900 hover:opacity-90' :
              cfg.id === 'trader' ? 'bg-white text-gray-900 hover:bg-gray-100' :
              cfg.id === 'pro' ? 'bg-violet-500 text-white hover:bg-violet-400' :
              'bg-blue-500 text-white hover:bg-blue-400'
            }`}
          >
            Start with {cfg.label}
          </button>
        </div>
      </div>
    </div>
  );
}
